import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { money, fmtDDMMYYYY } from '../../lib/format';
import { IconEye, IconEdit, IconTrash } from '../components/SvgIcons';

export default function ClientList({ currency, canViewFinance = true, onViewClient, onEditClient, onAddClient }) {
  const [clients, setClients] = useState([]);
  const [referenceSources, setReferenceSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [referenceFilter, setReferenceFilter] = useState('All');
  const [sortBy, setSortBy] = useState('name');
  const [toast, setToast] = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const loadClients = () => {
    setLoading(true);
    Promise.all([
      api.get('/api/client-module/clients'),
      api.get('/api/client-module/references'),
    ])
      .then(([clientData, refData]) => {
        setClients(clientData || []);
        setReferenceSources(refData || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(loadClients, []);
  useEffect(() => {
    const handler = () => loadClients();
    window.addEventListener('clientSaved', handler);
    return () => window.removeEventListener('clientSaved', handler);
  }, []);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete client "${name}"? All their service orders will also be removed.`)) return;
    try {
      await api.del(`/api/client-module/clients/${id}`);
      showToast('Client deleted successfully');
      loadClients();
    } catch (err) {
      alert(err.message || 'Failed to delete client');
    }
  };

  // Aggregate financials from serviceOrders
  function getClientFinancials(c) {
    const orders = c.serviceOrders || [];
    const totalRevenue = orders.reduce((s, o) => s + (o.finalAmount || 0), 0);
    const totalReceived = orders.reduce((s, o) => s + (o.amountReceived || 0), 0);
    const totalPending = orders.reduce((s, o) => s + (o.pendingAmount || 0), 0);
    const activeOrders = orders.filter((o) => o.workStatus === 'In Progress').length;
    const nextRenewal = orders
      .filter((o) => o.renewalDate)
      .map((o) => o.renewalDate)
      .sort()
      .find(Boolean);
    return { totalRevenue, totalReceived, totalPending, activeOrders, nextRenewal };
  }

  // Overall Stats
  const activeClientsCount = clients.filter((c) => c.status === 'Active').length;
  const allOrders = clients.flatMap((c) => c.serviceOrders || []);
  const inProgressCount = allOrders.filter((o) => o.workStatus === 'In Progress').length;
  const totalRevAll = allOrders.reduce((s, o) => s + (o.finalAmount || 0), 0);
  const totalRecAll = allOrders.reduce((s, o) => s + (o.amountReceived || 0), 0);
  const totalPendAll = allOrders.reduce((s, o) => s + (o.pendingAmount || 0), 0);

  // Filter & Sort
  const filtered = clients.filter((c) => {
    const matchSearch = !search || c.clientName.toLowerCase().includes(search.toLowerCase()) ||
      (c.company && c.company.toLowerCase().includes(search.toLowerCase())) ||
      (c.mobile && c.mobile.includes(search));
    const matchStatus = statusFilter === 'All' || c.status === statusFilter;
    const matchRef = referenceFilter === 'All' || c.referenceSource === referenceFilter;
    return matchSearch && matchStatus && matchRef;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'name') return a.clientName.localeCompare(b.clientName);
    if (sortBy === 'orders') return (b.serviceOrders?.length || 0) - (a.serviceOrders?.length || 0);
    if (sortBy === 'revenue') {
      const aRev = (a.serviceOrders || []).reduce((s, o) => s + (o.finalAmount || 0), 0);
      const bRev = (b.serviceOrders || []).reduce((s, o) => s + (o.finalAmount || 0), 0);
      return bRev - aRev;
    }
    if (sortBy === 'date') return (b.joinDate || '').localeCompare(a.joinDate || '');
    return 0;
  });
  return (
    <div>
      {toast && (
        <div style={{ background: 'var(--crm-emerald-light)', color: 'var(--crm-emerald)', padding: '10px 18px', borderRadius: 10, marginBottom: 16, fontWeight: 700, border: '1px solid var(--crm-border)' }}>
          ✓ {toast}
        </div>
      )}

      {/* Metric Cards */}
      <div className="cm-ref-stats-row">
        <div className="cm-ref-stat-card">
          <div className="cm-ref-stat-label">ACTIVE CLIENTS</div>
          <div className="cm-ref-stat-val">{activeClientsCount}</div>
        </div>
        <div className="cm-ref-stat-card">
          <div className="cm-ref-stat-label">ACTIVE SERVICE ORDERS</div>
          <div className="cm-ref-stat-val">{inProgressCount}</div>
        </div>
        {canViewFinance && (
          <>
            <div className="cm-ref-stat-card">
              <div className="cm-ref-stat-label">TOTAL REVENUE</div>
              <div className="cm-ref-stat-val">
                {money(totalRevAll, currency)}
              </div>
            </div>
            <div className="cm-ref-stat-card">
              <div className="cm-ref-stat-label">RECEIVED TILL DATE</div>
              <div className="cm-ref-stat-val" style={{ color: 'var(--crm-emerald)' }}>
                {money(totalRecAll, currency)}
              </div>
            </div>
            <div className="cm-ref-stat-card">
              <div className="cm-ref-stat-label">PENDING BALANCE</div>
              <div className="cm-ref-stat-val" style={{ color: 'var(--crm-text-main)' }}>
                {money(totalPendAll, currency)}
              </div>
            </div>
          </>
        )}
      </div>
      {/* Search & Filter Bar */}
      <div className="cm-ref-toolbar">
        <input
          className="cm-ref-search"
          placeholder="Search client name, company, or phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select className="cm-select-filter" value={referenceFilter} onChange={(e) => setReferenceFilter(e.target.value)}>
            <option value="All">All References</option>
            {referenceSources.map((r) => <option key={r.id} value={r.sourceName}>{r.sourceName}</option>)}
          </select>
          <select className="cm-select-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          <select className="cm-select-filter" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="name">Sort by Name</option>
            <option value="orders">Sort by # Orders</option>
            <option value="revenue">Sort by Revenue</option>
            <option value="date">Sort by Join Date</option>
          </select>
          <a href="/api/client-module/export-csv" target="_blank" rel="noreferrer" className="cm-btn-outline"
            style={{ background: 'var(--crm-emerald-light)', color: 'var(--crm-emerald)', borderColor: '#dbeafe', textDecoration: 'none', fontSize: '0.82rem' }}>
            Export CSV
          </a>
        </div>
      </div>

      {/* Client Cards */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--crm-text-muted)', fontWeight: 600 }}>Loading CRM Client Directory…</div>
      ) : sorted.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--crm-text-muted)', background: 'var(--crm-card-bg)', borderRadius: 12, border: '1px solid var(--crm-border)' }}>
          No clients found. Click "+ Add New Client" to create one.
        </div>
      ) : (
        sorted.map((c) => {
          const fin = getClientFinancials(c);
          const orders = c.serviceOrders || [];
          const latestOrder = orders[orders.length - 1];

          return (
            <div
              className="cm-client-card-row"
              key={c.id || c.clientName}
              onClick={() => onViewClient(c.id)}
              style={{ cursor: 'pointer', transition: 'box-shadow .15s' }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(34,78,174,0.12)'; e.currentTarget.style.borderColor = 'var(--crm-emerald)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = ''; }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="cm-card-title-group">
                  <span className="cm-toggle-indicator" title={c.status} style={{ background: c.status === 'Active' ? '#224eae' : '#94a3b8' }} />
                  <span className="cm-card-title">{c.clientName}</span>
                  {c.company && c.company !== c.clientName && (
                    <span style={{ fontSize: '0.8rem', color: 'var(--crm-text-muted)' }}>{c.company}</span>
                  )}
                  <span style={{ fontSize: '0.75rem', color: 'var(--crm-text-muted)', fontWeight: 600 }}>#{c.id?.slice(-5)}</span>
                </div>

                <div className="cm-card-tags">
                  <span className="cm-card-tag-item">Ref: {c.referenceSource || 'Direct'}</span>
                  <span className="cm-card-tag-item" style={{ background: 'var(--crm-blue-bg)', color: 'var(--crm-blue-text)', border: '1px solid var(--crm-blue-border)' }}>
                    {orders.length} Order{orders.length !== 1 ? 's' : ''}
                  </span>
                  {fin.activeOrders > 0 && (
                    <span className="cm-card-tag-item" style={{ background: 'var(--crm-green-bg)', color: 'var(--crm-green-text)', border: '1px solid var(--crm-green-border)' }}>
                      {fin.activeOrders} Active
                    </span>
                  )}
                  {latestOrder && (
                    <span className="cm-card-tag-item" style={{ background: 'var(--crm-purple-bg)', color: 'var(--crm-purple-text)', border: '1px solid var(--crm-purple-border)' }}>
                      Latest: {latestOrder.serviceName}
                    </span>
                  )}
                </div>

                <div className="cm-card-meta-line">
                  Joined: <strong>{fmtDDMMYYYY(c.joinDate)}</strong>
                  {fin.nextRenewal && (
                    <> · Next Renewal: <strong style={{ color: 'var(--crm-text-main)' }}>{fmtDDMMYYYY(fin.nextRenewal)}</strong></>
                  )}
                  {c.mobile && <> · {c.mobile}</>}
                </div>
              </div>

              {canViewFinance ? (
                <div className="cm-card-right-stats" onClick={(e) => e.stopPropagation()}>
                  <>
                    <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--crm-text-main)' }}>{money(fin.totalRevenue, currency)}</div>
                    <div style={{ fontSize: '0.775rem', color: 'var(--crm-text-muted)', fontWeight: 700 }}>
                      Recv: {money(fin.totalReceived, currency)} | Pend: <span style={{ color: 'var(--crm-text-main)' }}>{money(fin.totalPending, currency)}</span>
                    </div>
                  </>
                  <div style={{ marginTop: 8, display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button className="cm-action-icon-btn" title="View Profile" onClick={() => onViewClient(c.id)}>
                      <IconEye size={16} />
                    </button>
                    <button className="cm-action-icon-btn" title="Edit Client Info" onClick={() => onEditClient(c)}>
                      <IconEdit size={16} />
                    </button>
                    <button className="cm-action-icon-btn" title="Delete Client" style={{ color: 'var(--crm-text-muted)' }} onClick={() => handleDelete(c.id, c.clientName)}>
                      <IconTrash size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} onClick={(e) => e.stopPropagation()}>
                  <button className="cm-btn-emerald" style={{ fontSize: '0.82rem', padding: '6px 14px' }} onClick={() => onViewClient(c.id)}>
                    View Profile →
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
